// EDGE 계정 하나로 접근하는 앱 목록. 서버 EdgeApps 와 id 를 맞춰야 한다 (토큰 aud 값).
// 회사별 On/Off 는 회사 상세에서 관리하고, 로그인 토큰의 aud 에 켜진 앱만 실린다.
export interface EdgeApp {
  id: string
  label: string
  description: string
  /** 앱 선택 화면·포털에서 쓰는 모노그램(정사각 타일에 들어가는 두 글자) */
  mark: string
  /** 진입 URL. 같은 origin 경로면 토큰(localStorage)이 공유되어 추가 로그인이 필요 없다. */
  url: string
  /** 이 SPA 자신이면 라우터 이동, 아니면 전체 페이지 이동 */
  internal: boolean
}

export const APP_EDGEMANAGER = 'EdgeManager'
export const APP_BOARD = 'board'
export const APP_BUILDER = 'builder'

// ── 앱 단위 추가 권한(scope) — 서버 EdgeScopes 와 값을 맞춘다 ──────────────
// 역할 Admin 은 scope 와 무관하게 전 영역 통과. scope 는 Admin 이 아닌 사용자(Edge팀 등)에게
// 영역별 전 회사 권한을 얹는다. 소속 회사는 그대로라 보드 등 다른 앱에서는 그 회사의 일반 사용자.
// (2026-09-03 우산 edge:all-companies 를 영역별로 세분화)
export const SCOPE_EDGE_USERS = 'edge:users'
export const SCOPE_EDGE_COMPANIES = 'edge:companies'
export const SCOPE_EDGE_SERVERS = 'edge:servers'
export const SCOPE_EDGE_LICENSES = 'edge:licenses'
export const SCOPE_SURVEYS_VIEW = 'surveys:view'
export const SCOPE_SURVEYS_MANAGE = 'surveys:manage'
// 보드 — 다른 scope 와 달리 "전 회사"가 아니라 소속 회사 범위 (부서장·경영진용 열람)
export const SCOPE_BOARD_READ_ALL = 'board:read-all'

/** 폐지된 우산 scope — 세분화 배포 직전 발급 토큰(수명 ≤30분)의 권한이 끊기지 않게 전환기 동안 세부 전체로 인정 */
const LEGACY_ALL_COMPANIES = ['edge:all-companies', 'edgemanager:all-companies']

/** 부여 화면(사용자 수정 모달)용 목록. short 는 테이블 배지 라벨. */
export interface ScopeDef { value: string; label: string; hint: string; short: string }
export const SCOPE_DEFS: ScopeDef[] = [
  { value: SCOPE_EDGE_USERS, label: '사용자 관리', hint: '전 회사 사용자 열람·초대·수정 (포털)', short: '사용자' },
  { value: SCOPE_EDGE_COMPANIES, label: '회사 관리', hint: '회사 생성·수정·서비스 구독 (포털)', short: '회사' },
  { value: SCOPE_EDGE_SERVERS, label: '서버 관리', hint: '전 회사 Edge 서버·원격 명령·Harbor', short: '서버' },
  { value: SCOPE_EDGE_LICENSES, label: '라이센스', hint: '라이센스 발급·삭제', short: '라이센스' },
  { value: SCOPE_SURVEYS_VIEW, label: '설비조사 열람', hint: '설비조사 전체 열람', short: '조사열람' },
  { value: SCOPE_SURVEYS_MANAGE, label: '설비조사 관리', hint: '열람 포함 — 업로드·수정·삭제', short: '조사관리' },
  { value: SCOPE_BOARD_READ_ALL, label: '보드 전 팀 열람', hint: '자기 회사 보드의 모든 팀 프로젝트를 읽기 전용으로 (부서장·경영진용)', short: '보드열람' },
]

/** held scope 가 wanted 권한을 포함하는가 — 서버 EdgeScopes.Grants 와 같은 규칙
 * (manage ⊇ view, 폐지된 우산 ⊇ 세분화 당시 세부 전체 — 이후 추가된 board: 계열은 딸려가지 않음) */
export const scopeGrants = (held: string, wanted: string): boolean =>
  held === wanted ||
  (held === SCOPE_SURVEYS_MANAGE && wanted === SCOPE_SURVEYS_VIEW) ||
  (LEGACY_ALL_COMPANIES.includes(held) && wanted !== SCOPE_BOARD_READ_ALL)

/** Edge팀 표시용 — 플랫폼(edge:) 계열 scope 를 하나라도 갖고 있는가 (폐지된 우산 포함) */
export const hasEdgeTeamScope = (scopes: string[]): boolean =>
  scopes.some(s => s.startsWith('edge:') || s === 'edgemanager:all-companies')

export const EDGE_APPS: EdgeApp[] = [
  {
    id: APP_EDGEMANAGER,
    label: 'EdgeManager',
    description: '엣지 서버와 라이센스, 설비조사를 관리합니다',
    mark: 'EM',
    url: '/',
    internal: true,
  },
  {
    id: APP_BOARD,
    label: '보드',
    description: '팀 업무를 칸반, 캘린더, 타임라인으로 봅니다',
    mark: 'BD',
    // 게이트웨이에서 /board/ 를 보드 웹으로 라우팅하는 것이 기본. 다른 origin 이면 빌드 시 VITE_BOARD_URL 로 덮어쓴다.
    url: import.meta.env.VITE_BOARD_URL || '/board/',
    internal: false,
  },
  {
    id: APP_BUILDER,
    label: 'Builder',
    description: '현장 장비 수집 · 전송 프로젝트를 만들고 현장 PC 에 반영합니다 (Edge팀)',
    mark: 'EB',
    // 게이트웨이에서 /builder/ 를 EdgeBuilder 로 라우팅. 다른 origin 이면 빌드 시 VITE_BUILDER_URL 로 덮어쓴다.
    url: import.meta.env.VITE_BUILDER_URL || '/builder/',
    internal: false,
  },
]

/** 앱으로 이동. 같은 SPA 면 라우터, 다른 앱이면 전체 페이지 이동(같은 origin 경로면 토큰이 localStorage 로 공유됨). */
export function openApp(app: EdgeApp, navigate: (to: string) => void) {
  if (app.internal) navigate(app.url)
  else window.location.assign(app.url)
}
